import {
	ReactElement,
	PropsWithChildren,
	useMemo,
	UIEvent,
	forwardRef,
	Ref,
	useImperativeHandle,
	useRef,
	RefObject,
	Fragment
} from "react";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import {
	AppIcon,
	Button,
	ButtonBase,
	DropOverActionList,
	FormattedText,
	Icon,
	IconButton,
	NoResults,
	NoResultsProps,
	SearchInput,
	SearchInputTrigger
} from "client/Components";
import { Guid } from "client_legacy/ndaw/guid";
import { localize } from "client_legacy/ndaw/culture";
import { TileTabSelector } from "./TileTabSelector";
import styles from "./LiveTileContent.module.less";
import classNames from "classnames";
import { useToggle } from "client/Hooks";
import {
	generateTrackingId,
	isNotNil,
	isNotNilOrWhiteSpace,
	setTrackingId
} from "client/Misc/utils";
import { Action, reduceToAvailableActions } from "client_legacy/ndaw/actions";
import { actionComponentFactory } from "client/Components/ActionList/actionsRenderHelpers";
import { LiveTileOverlay } from "./Layout/LiveTileOverlay";
import { LiveTileOverlayState } from "./useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileMenuButton } from "./TileMenuButton";

type TileHeaderWithoutActions = {};

type TileHeaderWithActions<TContext> = {
	actions: readonly Action<TContext>[];
	actionContext: TContext;
};

type TileHeaderProps<TContext> = {
	icon: AppImageDisplayInfo;
} & (
		| TileHeaderWithoutActions
		| TileHeaderWithActions<TContext>
	);

const TileHeader = <TContext,>(props: PropsWithChildren<TileHeaderProps<TContext>>): ReactElement => {
	const {
		icon,
		children
	} = props;

	return (
		<div className={styles.header}>
			<div className={styles.headerAppIcon}>
				<AppIcon value={icon} color="inherit" />
			</div>
			{children}

			{hasActions(props) && (
				<TileHeaderActions<TContext> {...props} />
			)}
		</div>
	);
};
TileHeader.displayName = "TileHeader";

const TileHeaderActions = <TContext,>(props: TileHeaderWithActions<TContext>): ReactElement | null => {
	const [isOpen, toggleIsOpen] = useToggle();
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const availableActions = useMemo(
		(): TileHeaderWithActions<TContext> | undefined => {
			const { actions, actionContext } = props;
			const availableActions: readonly Action<TContext>[] = reduceToAvailableActions(
				actions,
				actionContext
			);

			if (availableActions.length === 0) {
				return undefined;
			}

			return {
				actions: availableActions,
				actionContext
			};
		},
		[props]
	);

	if (!availableActions) {
		return null;
	}

	return (
		<div className={styles.actions}>
			<IconButton
				shape="round"
				kind="transparent-colored"
				icon={{
					kind: "font",
					name: "ellipsis",
					color: "inherit"
				}}
				trackingId="actions"
				onClick={() => toggleIsOpen()}
				ref={ref}
				tooltip={localize("_More")}
				isActive={isOpen}
			/>

			{isOpen && ref.current && (
				<DropOverActionList
					trackingId={generateTrackingId("actions", "__dropover")}
					target={ref.current}
					targetAlignment="br"
					contentAlignment="tr"
					onClose={() => toggleIsOpen(false)}
					bubbleClick={false}
					menuTitle={localize("_More")}
				>
					{availableActions.actions
						.map(
							(x): ReactElement => (
								<Fragment key={x.name}>
									{actionComponentFactory(x, availableActions.actionContext)}
								</Fragment>
							)
						)
					}
				</DropOverActionList>
			)}
		</div>
	);
};
TileHeaderActions.displayName = "TileHeaderActions";

function hasActions<TContext>(props: TileHeaderWithoutActions | TileHeaderWithActions<TContext>): props is TileHeaderWithActions<TContext> {
	return isNotNil((props as TileHeaderWithActions<TContext>).actions);
}

type TileBackHeaderProps = {
	onClick: () => void;
};

const TileBackHeader = (props: TileBackHeaderProps): ReactElement => {
	const {
		onClick
	} = props;

	return (
		<div className={styles.header}>
			<ButtonBase
				onClick={() => onClick()}
				trackingId="back"
				className={styles.backButton}
			>
				<Icon value="chevron-left" />

				<h4>
					{localize("_Back")}
				</h4>
			</ButtonBase>
		</div>
	);
};
TileBackHeader.displayName = "TileBackHeader";

export type TileTitleProps = {
	text: string;
	suffix?: string;
};

const TileTitle = (props: PropsWithChildren<TileTitleProps>): ReactElement => {
	const {
		text,
		suffix
	} = props;

	return (
		<h4
			{...setTrackingId("title")}
			className={styles.title}
		>
			<span>{ text }</span>
			{suffix && (
				<span className={styles.titleSuffix}>{ suffix }</span>
			)}
		</h4>
	);
};
TileTitle.displayName = "TileTitle";

const TileContent = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.content}>
			{ children }
		</div>
	);
};
TileContent.displayName = "TileContent";

type TileFooterProps = {
	name: string;
	onClick: () => void;
};

const TileFooter = (props: TileFooterProps): ReactElement => {
	const {
		name,
		onClick
	} = props;

	return (
		<ButtonBase
			trackingId="footer"
			className={styles.footer}
			onClick={() => onClick()}
		>
			<span className={styles.footerText}>
				<FormattedText
					text={localize("_OpenName")}
					thingsToReplace={[
						{ what: "{{Name}}", filler: name }
					]}
				/>
			</span>

			<Icon value="arrow-right" size="small" />
		</ButtonBase>
	);
};
TileFooter.displayName = "TileFooter";

const TileBusyIndicatorContainer = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.busyIndicatorContainer}>
			{ children }
		</div>
	);
};
TileBusyIndicatorContainer.displayName = "TileBusyIndicatorContainer";

export type TileScrollableHandle = {
	scrollTo(y: number): void;
};

export type TileScrollableProps = {
	onScroll?: (scrollTop: number) => void;
};

const TileScrollable = forwardRef<TileScrollableHandle, PropsWithChildren<TileScrollableProps>>(
	(props: PropsWithChildren<TileScrollableProps>, ref: Ref<TileScrollableHandle>): ReactElement => {
		const {
			onScroll,
			children
		} = props;

		const divRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		const scrollHandler = useMemo(
			(): ((args: UIEvent<HTMLDivElement>) => void) | undefined => {
				if (onScroll) {
					return (args) => {
						const { scrollTop } = args.currentTarget;

						onScroll(scrollTop);
					};
				}

				return undefined;
			},
			[onScroll]
		);

		useImperativeHandle(
			ref,
			(): TileScrollableHandle => ({
				scrollTo: (y: number) => {
					if (divRef.current) {
						divRef.current
							.scrollTo(0, y);
					}
				}
			}),
			[]
		);

		return (
			<div
				className={styles.scrollable}
				onScroll={scrollHandler}
				ref={divRef}
			>
				{ children }
			</div>
		);
	}
);
TileScrollable.displayName = "TileScrollable";

export type TileContentHeaderProps = {
	text: string;
	isHighlighted?: boolean;
};

const TileContentHeader = (props: TileContentHeaderProps): ReactElement => {
	const {
		text,
		isHighlighted = false
	} = props;

	return (
		<h4
			className={
				classNames(
					styles.contentHeader,
					{
						[styles.contentHeader_isHighlighted]: isHighlighted
					}
				)
			}
		>
			{ text }
		</h4>
	);
};
TileContentHeader.displayName = "TileContentHeader";

export type TileListProps = {
	trackingId?: string;
};

const TileList = (props: PropsWithChildren<TileListProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={styles.list}
		>
			{ children }
		</ul>
	);
};
TileList.displayName = "TileList";

export type TileMessageProps = {
	text: string | ReactElement;
};

const TileMessage = (props: TileMessageProps): ReactElement => {
	const {
		text
	} = props;

	return (
		<p
			{...setTrackingId("error-message")}
			className={styles.errorMessage}
		>
			{ text }
		</p>
	);
};
TileMessage.displayName = "TileMessage";

export type TileNoResultsProps = Omit<NoResultsProps, "height" | "hasExtraTopPadding">;

export const TileNoResults = (props: TileNoResultsProps): ReactElement => {
	return (
		<div className={styles.noResultsContainer}>
			<NoResults
				{...props}
			/>
		</div>
	);
};
TileNoResults.displayName = "TileNoResults";

export type TileContentStackProps = {
	isHidden?: boolean;
};

const TileContentStack = (props: PropsWithChildren<TileContentStackProps>): ReactElement => {
	const {
		isHidden = false,
		children
	} = props;

	return (
		<div
			className={
				classNames(
					styles.contentStack,
					{
						[styles.contentStack_hidden]: isHidden
					}
				)
			}
		>
			{ children }
		</div>
	);
};
TileContentStack.displayName = "TileContentStack";

export type TileLoadMoreButtonProps = {
	onClick(): void;
};

const TileLoadMoreButton = (props: TileLoadMoreButtonProps): ReactElement => {
	const {
		onClick
	} = props;

	return (
		<div className={styles.loadMoreButton}>
			<Button
				kind="primary"
				color="app"
				text={localize("_LoadMore")}
				onClick={onClick}
				width="fill"
			/>
		</div>
	);
};
TileLoadMoreButton.displayName = "TileLoadMoreButton";

export type TileSearchInputProps = {
	value?: VersionedValue<string>;
	onChange(value: string, version: Guid): void;
	placeholder?: string;
	trigger?: SearchInputTrigger;
	trackingId?: string;
};

const TileSearchInput = (props: TileSearchInputProps): ReactElement => {
	const {
		value,
		onChange,
		placeholder,
		trigger,
		trackingId
	} = props;

	return (
		<div className={styles.search}>
			<SearchInput
				value={value}
				onChange={onChange}
				placeholder={placeholder}
				trigger={trigger}
				trackingId={trackingId}
				isBorderless
				height="fill"
				variant="white"
			/>
		</div>
	);
};
TileSearchInput.displayName = "TileSearchInput";

export type LiveTileContentProps = {
	icon: AppImageDisplayInfo;
	name: string;
	overlay: LiveTileOverlayState;
	onOpenTileMenu: (coordinate: Coordinate) => void;
	overlayStatusIcon: ReactElement;
	isPoppedOut?: boolean;
};

export const LiveTileContent = (props: PropsWithChildren<LiveTileContentProps>): ReactElement => {
	const {
		icon,
		name,
		overlay,
		onOpenTileMenu,
		overlayStatusIcon,
		isPoppedOut = false,
		children
	} = props;

	return (
		<div
			className={
				classNames(
					styles.root,
					{ [styles.root_poppedOut]: isPoppedOut }
				)
			}
		>
			{overlay.isVisible && (
				<div
					className={
						classNames(
							styles.overlay,
							{
								[styles.overlay_clickable]: isNotNil(overlay.onClick),
								[styles.overlay_hoverable]: isNotNil(overlay.onClick)
									|| !overlay.isDisabled,
								[styles.overlay_disabled]: overlay.isDisabled,
								[styles.overlay_hideContent]: !overlay.shouldNotHideContent
							}
						)
					}
					onClick={overlay.onClick}
				>
					<div className={styles.overlayHeader}>
						<div className={
							classNames({
								[styles.statusIcon_shouldHideOnHover]: overlay.shouldShowMenuButton
							})
						}>
							{overlayStatusIcon}
						</div>
						{overlay.shouldShowMenuButton && (
							<div className={styles.menuButton}>
								<TileMenuButton
									onClick={onOpenTileMenu}
								/>
							</div>
						)}
					</div>
					<LiveTileOverlay
						icon={icon}
						name={name}
						isDisabled={overlay.isDisabled}
						hasOneCellHeight={overlay.hasOneCellHeight}
						tooltip={
							isNotNilOrWhiteSpace(overlay.tooltip)
								? overlay.tooltip
								: name
						}
					/>
				</div>
			)}
			{ children }
		</div>
	);
};
LiveTileContent.displayName = "LiveTileContent";

LiveTileContent.Header = TileHeader;
LiveTileContent.BackHeader = TileBackHeader;
LiveTileContent.Title = TileTitle;
LiveTileContent.Content = TileContent;
LiveTileContent.Footer = TileFooter;
LiveTileContent.Scrollable = TileScrollable;
LiveTileContent.ContentHeader = TileContentHeader;
LiveTileContent.List = TileList;
LiveTileContent.Message = TileMessage;
LiveTileContent.NoResults = TileNoResults;
LiveTileContent.ContentStack = TileContentStack;
LiveTileContent.SearchInput = TileSearchInput;
LiveTileContent.TabSelector = TileTabSelector;
LiveTileContent.LoadMoreButton = TileLoadMoreButton;
LiveTileContent.BusyIndicatorContainer = TileBusyIndicatorContainer;
